import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type {
  Tender,
  TenderSearchResponse,
  TenderVersion,
  VTenderLatestVersion,
  VTendersPublic,
} from '@/lib/pocketbase'

// Tender list query options
export interface TenderFilters {
  search?: string
  status?: string[]
  province?: string[]
  agencyId?: string
  procurementMethod?: string[]
  minBudget?: number
  maxBudget?: number
  dateFrom?: string
  dateTo?: string
  tags?: string[]
  sort?: string
  page?: number
  perPage?: number
}

interface QueryHookOptions {
  enabled?: boolean
}

function resolveTenderId(item: {
  tender?: string
  tender_id?: string
  id?: string
}) {
  return (item.tender || item.tender_id || item.id || '').trim()
}

function mapTenderRecord(item: VTendersPublic) {
  return {
    ...item,
    tender_id: resolveTenderId(item),
    project_id: item.project_id ?? item.egp_project_no,
    project_name: item.project_name ?? item.title_th,
    dept_name: item.dept_name,
    province_code: item.province_code,
    sum_price_agree:
      item.sum_price_agree ??
      item.procured_amount_satang ??
      item.budget_amount_satang,
  }
}

// Fetch tender list from public view
function buildTenderFilter(
  filters: TenderFilters,
  options?: { includeStatus?: boolean }
) {
  const includeStatus = options?.includeStatus ?? true
  const {
    search,
    status,
    province,
    procurementMethod,
    minBudget,
    maxBudget,
    dateFrom,
    dateTo,
  } = filters
  const filterParts: string[] = []
  // `sum_price_agree` is returned by the view but not filterable in PB query expressions.
  // Keep budget filtering on the raw satang fields to avoid 400 errors.
  const budgetFields = ['procured_amount_satang', 'budget_amount_satang']
  const dateFields = ['published_at']

  if (search) {
    filterParts.push(
      pb.filter(
        '(title_th ~ {:search} || project_name ~ {:search} || egp_project_no ~ {:search} || dept_name ~ {:search})',
        {
        search,
        }
      )
    )
  }
  if (includeStatus && status && status.length > 0) {
    const params = Object.fromEntries(status.map((value, index) => [`status${index}`, value]))
    const statusFilter = status.map((_, index) => `project_status = {:status${index}}`).join(' || ')
    filterParts.push(pb.filter(`(${statusFilter})`, params))
  }
  if (province && province.length > 0) {
    const params = Object.fromEntries(
      province.map((value, index) => [`province${index}`, value])
    )
    const provinceFilter = province
      .map((_, index) => `province_code = {:province${index}}`)
      .join(' || ')
    filterParts.push(pb.filter(`(${provinceFilter})`, params))
  }
  if (procurementMethod && procurementMethod.length > 0) {
    const params = Object.fromEntries(procurementMethod.map((value, index) => [`method${index}`, value]))
    const methodFilter = procurementMethod.map((_, index) => `procurement_method = {:method${index}}`).join(' || ')
    filterParts.push(pb.filter(`(${methodFilter})`, params))
  }
  if (typeof minBudget === 'number' && Number.isFinite(minBudget)) {
    const minFilter = budgetFields.map((field) => `${field} >= {:minBudget}`).join(' || ')
    filterParts.push(pb.filter(`(${minFilter})`, { minBudget }))
  }
  if (typeof maxBudget === 'number' && Number.isFinite(maxBudget)) {
    const maxFilter = budgetFields.map((field) => `${field} <= {:maxBudget}`).join(' || ')
    filterParts.push(pb.filter(`(${maxFilter})`, { maxBudget }))
  }
  if (dateFrom) {
    const fromFilter = dateFields.map((field) => `${field} >= {:dateFrom}`).join(' || ')
    filterParts.push(pb.filter(`(${fromFilter})`, { dateFrom }))
  }
  if (dateTo) {
    const toFilter = dateFields.map((field) => `${field} <= {:dateTo}`).join(' || ')
    filterParts.push(pb.filter(`(${toFilter})`, { dateTo }))
  }

  return filterParts.join(' && ')
}

function buildTenderSearchQueryString(filters: TenderFilters = {}) {
  const params = new URLSearchParams()
  const { search, status, province, procurementMethod, minBudget, maxBudget, dateFrom, dateTo, sort, page, perPage } = filters

  if (search) params.set('q', search)
  status?.forEach((value) => params.append('status', value))
  province?.forEach((value) => params.append('province', value))
  procurementMethod?.forEach((value) => params.append('method', value))
  if (typeof minBudget === 'number' && Number.isFinite(minBudget)) {
    params.set('minBudget', String(Math.trunc(minBudget)))
  }
  if (typeof maxBudget === 'number' && Number.isFinite(maxBudget)) {
    params.set('maxBudget', String(Math.trunc(maxBudget)))
  }
  if (dateFrom) params.set('dateFrom', dateFrom)
  if (dateTo) params.set('dateTo', dateTo)
  if (sort) params.set('sort', sort)
  if (page) params.set('page', String(page))
  if (perPage) params.set('perPage', String(perPage))

  return params.toString()
}

function normalizeTenderSearchResponse(response: TenderSearchResponse) {
  return {
    ...response,
    items: (response.items || []).map((item) => ({
      ...item,
      tender: item.tender_id || item.id,
      tender_id: resolveTenderId(item as any),
      project_id: (item as any).project_id ?? item.egp_project_no,
      project_name: item.project_name ?? item.title_th,
      dept_name: item.dept_name,
      sum_price_agree: item.sum_price_agree ?? item.procured_amount_satang ?? item.budget_amount_satang ?? 0,
      created: (item as any).created ?? '',
      updated: (item as any).updated ?? '',
    })),
    stats: {
      totalCount: response.stats?.totalCount ?? response.totalItems ?? 0,
      totalBudget: response.stats?.totalBudget ?? 0,
      biddingCount: response.stats?.biddingCount ?? 0,
    },
  }
}

export function useTenderSearchV2(filters: TenderFilters = {}, options?: QueryHookOptions) {
  return useQuery({
    queryKey: ['tenders-v2', filters],
    queryFn: async () => {
      const queryString = buildTenderSearchQueryString(filters)
      const endpoint = queryString ? `/api/tenders/search?${queryString}` : '/api/tenders/search'
      const result = await pb.send<TenderSearchResponse>(endpoint, { method: 'GET' })
      return normalizeTenderSearchResponse(result)
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

export function useTenderList(filters: TenderFilters = {}, options?: QueryHookOptions) {
  const {
    page = 1,
    perPage = 20,
    sort = '-published_at',
  } = filters

  return useQuery({
    queryKey: ['tenders', filters],
    queryFn: async () => {
      const filter = buildTenderFilter(filters, { includeStatus: true })

      const result = await pb.collection('v_tenders_public').getList<VTendersPublic>(
        page,
        perPage,
        {
          filter,
          sort,
          fields:
            'id,tender,tender_id,egp_project_no,project_id,project_name,title_th,dept_name,province_code,procurement_method,project_status,budget_amount_satang,procured_amount_satang,sum_price_agree,published_at,version_number,created,updated',
        }
      )

      return { ...result, items: result.items.map(mapTenderRecord) }
    },
    staleTime: 30_000, // 30 seconds
    enabled: options?.enabled ?? true,
  })
}

export function useTenderStats(
  filters: TenderFilters = {},
  options?: QueryHookOptions
) {
  const {
    search,
    status,
    procurementMethod,
    province,
    minBudget,
    maxBudget,
    dateFrom,
    dateTo,
    tags,
  } = filters

  return useQuery({
    queryKey: [
      'tender-stats',
      { search, status, procurementMethod, province, minBudget, maxBudget, dateFrom, dateTo, tags },
    ],
    queryFn: async () => {
      const queryString = buildTenderSearchQueryString({
        ...filters,
        page: 1,
        perPage: 1,
      })
      const endpoint = queryString
        ? `/api/tenders/search?${queryString}`
        : '/api/tenders/search'
      const result = await pb.send<TenderSearchResponse>(endpoint, { method: 'GET' })
      return {
        totalCount: result.stats?.totalCount ?? result.totalItems ?? 0,
        totalBudget: result.stats?.totalBudget ?? 0,
        biddingCount: result.stats?.biddingCount ?? 0,
      }
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

// Fetch single tender with latest version
export function useTender(tenderId: string) {
  return useQuery({
    queryKey: ['tender', tenderId],
    queryFn: async () => {
      const tender = await pb.collection('tenders').getOne<Tender>(tenderId, {
        expand: 'agency,tags',
      })
      return tender
    },
    enabled: !!tenderId,
  })
}

// Fetch tender versions
export function useTenderVersions(tenderId: string) {
  return useQuery({
    queryKey: ['tender-versions', tenderId],
    queryFn: async () => {
      const result = await pb.collection('tender_versions').getList<TenderVersion>(1, 50, {
        filter: `tender = "${tenderId}"`,
        sort: '-version_seq',
      })
      return result.items
    },
    enabled: !!tenderId,
  })
}

// Fetch latest tender version
export function useTenderLatestVersion(tenderId: string) {
  return useQuery({
    queryKey: ['tender-latest-version', tenderId],
    queryFn: async () => {
      const result = await pb.collection('v_tender_latest_versions').getFirstListItem<VTenderLatestVersion>(
        `tender = "${tenderId}"`,
        { expand: 'tender,tender.agency' }
      )
      const resolvedTenderId = String((result as any).tender ?? (result as any).tender_id ?? tenderId)
      let tender = (result as any).expand?.tender as (Tender | undefined)
      let agency = (tender?.expand as any)?.agency

      // v_tender_latest_versions.tender is currently typed as json in schema,
      // so relation expand may be unavailable depending on server metadata.
      if (!tender) {
        try {
          tender = await pb.collection('tenders').getOne<Tender>(resolvedTenderId, {
            expand: 'agency',
          })
          agency = (tender?.expand as any)?.agency
        } catch {
          // keep view-only data if fallback read fails
        }
      }

      return {
        ...result,
        tender_id: resolvedTenderId,
        project_id:
          (result as any).project_id ??
          (result as any).egp_project_no ??
          (tender as any)?.project_id ??
          (tender as any)?.egp_project_no,
        project_name:
          (result as any).project_name ??
          (result as any).title_th ??
          (tender as any)?.project_name ??
          (tender as any)?.title_th,
        procurement_method:
          (result as any).procurement_method ??
          (tender as any)?.procurement_method,
        procurement_type:
          (result as any).procurement_type ??
          (tender as any)?.procurement_type,
        project_status: (result as any).project_status,
        province_code: (result as any).province_code ?? (tender as any)?.province_code,
        dept_name:
          (result as any).dept_name ??
          agency?.name_th ??
          agency?.name_en ??
          (result as any).dept_sub_name ??
          (tender as any)?.agency_name,
        dept_sub_name: (result as any).dept_sub_name,
        ministry: (result as any).ministry ?? (tender as any)?.ministry,
        budget_year:
          (result as any).budget_year ??
          (tender as any)?.budget_year ??
          (tender as any)?.budget_year_be,
        sum_price_agree: (result as any).sum_price_agree ?? (result as any).procured_amount_satang ?? (result as any).budget_amount_satang,
        price_build: (result as any).price_build,
        published_at: (result as any).published_at ?? (result as any).effective_at,
        version_number: (result as any).version_number ?? (result as any).version_seq,
      } as VTenderLatestVersion
    },
    enabled: !!tenderId,
  })
}

// Fetch tender status events (timeline)
export function useTenderTimeline(tenderId: string) {
  return useQuery({
    queryKey: ['tender-timeline', tenderId],
    queryFn: async () => {
      const result = await pb.collection('tender_status_events').getList(1, 100, {
        filter: `tender = "${tenderId}"`,
        sort: '-occurred_at',
      })
      return result.items
    },
    enabled: !!tenderId,
  })
}
